/**
 * Forward-message hook — re-sends M messages to N target conversations over
 * the real signal path (same as `useSendMessage`, but fan-out).
 *
 *   const { forward, isForwarding } = useForwardMessage()
 *   await forward({ body: "hello" }, [convA, convB])
 *   await forward([msgA, msgB, msgC], [convA])          // multi-select
 *   await forward({ body: null, attachments: [photo] }, [convA])
 *
 * For each (target, message): generate a temp uuid (= clientMsgId =
 * idempotency key), optimistically add the row to that conversation's wire
 * thread, push `message:send` over the conversation channel (signaling fans
 * out `message:new` live AND forwards to the storage pipeline), then
 * reconcile / rollback per copy independently.
 *
 * **Targets fan out in parallel; messages within one target go in sequence.**
 * A multi-select forward has to arrive in the order it was selected, and the
 * server timestamps on receipt — firing the whole batch at once would let the
 * thread reorder itself on reload. One slow target still can't hold up another.
 *
 * Attachments travel INLINE (url + metadata) exactly as on a first send —
 * bring-your-own-storage means the bytes already live at a host URL, so a
 * forward re-references them and nothing is re-uploaded.
 */

import { useCallback, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import type {
  Attachment,
  Message as WireMessage,
  MessageType,
} from '../services/messenger.wire'
import { sendMessageToConversation } from '../services/messenger.socket'
import { useMessengerStore } from '../store/messenger.store'

/** The parts of a message a forward re-sends. */
export interface ForwardSource {
  body: string | null
  /** Already-uploaded attachments to re-reference. Omit for a text forward. */
  attachments?: Attachment[]
  type?: MessageType
}

interface UseForwardMessage {
  /** One message or many; many are delivered in the order given. */
  forward: (
    source: ForwardSource | ForwardSource[],
    targetConversationIds: string[],
  ) => Promise<void>
  isForwarding: boolean
  error: string | null
}

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'temp-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function useForwardMessage(): UseForwardMessage {
  const me = useAuthStore((s) => s.user)
  // Optimistic `senderId` = contactPresenceId (chat identity); socket send still
  // authenticates as the auth user (`me.id`).
  const presenceId = useAuthStore((s) => s.presenceId)
  const addPending = useMessengerStore((s) => s.addPendingSend)
  const reconcile  = useMessengerStore((s) => s.reconcilePendingSend)
  const rollback   = useMessengerStore((s) => s.rollbackPendingSend)

  const [isForwarding, setIsForwarding] = useState<boolean>(false)
  const [error, setError]               = useState<string | null>(null)

  const forward = useCallback(
    async (
      source: ForwardSource | ForwardSource[],
      targetConversationIds: string[],
    ): Promise<void> => {
      if (!me?.id || !presenceId) { setError('not authenticated'); return }

      // An attachment-only message is a legitimate forward — gating on text
      // alone silently dropped every photo/document forward.
      const sources = (Array.isArray(source) ? source : [source]).filter(
        (src) => (src.body ?? '').trim() !== '' || (src.attachments?.length ?? 0) > 0,
      )
      if (sources.length === 0 || targetConversationIds.length === 0) return

      setError(null)
      setIsForwarding(true)

      /** One message → one conversation. Resolves false when the send failed. */
      const sendOne = async (
        conversationId: string,
        src: ForwardSource,
      ): Promise<boolean> => {
        const tempId = randomUuid()
        const now = new Date().toISOString()
        const text = (src.body ?? '').trim()
        const type: MessageType = src.type ?? 'text'
        // FRESH attachment ids per forwarded copy. The attachment `id` is the
        // storage primary key AND its idempotency key — storage-server inserts
        // with `onConflict('id').ignore()`. Re-sending the source id would
        // conflict with the original row (which still points at the original
        // `message_id`), so the insert is skipped and the forwarded message
        // persists with NO attachments: it looks right live, via the socket
        // echo, then comes back empty on reload or for anyone fetching history.
        // Per-target too — two targets sharing an id would collide with each
        // other. Generating them here (rather than letting signaling fill them
        // in) keeps the Kafka at-least-once retry dedupe that the id exists for.
        const copies: Attachment[] = (src.attachments ?? []).map((a) => ({
          ...a,
          id: randomUuid(),
        }))
        const temp: WireMessage = {
          id:          tempId,
          conversationId,
          senderId:    presenceId,
          type,
          body:        text || null,
          replyTo:     null,
          callPayload: null,
          editedAt:    null,
          deletedAt:   null,
          createdAt:   now,
          reactions:   [],
          attachments: copies,
        }
        addPending(temp)
        try {
          const { message } = await sendMessageToConversation(
            me.id,
            conversationId,
            {
              body: text || null,
              clientMsgId: tempId,
              messageType: type,
              replyTo: null,
              attachments: copies,
            },
            me.name,
          )
          reconcile(tempId, {
            ...temp,
            id:        message.id ?? tempId,
            body:      message.body ?? temp.body,
            createdAt: (message as { createdAt?: string }).createdAt ?? now,
          })
          return true
        } catch {
          rollback(tempId)
          return false
        }
      }

      // Targets in parallel — one failure doesn't block the rest. Messages
      // within a target strictly in sequence, so the batch keeps its order.
      const perTarget = await Promise.all(
        targetConversationIds.map(async (conversationId) => {
          let sent = 0
          for (const src of sources) {
            if (await sendOne(conversationId, src)) sent += 1
          }
          return sent
        }),
      )

      const expected = sources.length * targetConversationIds.length
      const delivered = perTarget.reduce((a, b) => a + b, 0)
      if (delivered < expected) {
        setError(`failed to forward ${expected - delivered} of ${expected} message(s)`)
      }
      setIsForwarding(false)
    },
    [me?.id, me?.name, presenceId, addPending, reconcile, rollback],
  )

  return { forward, isForwarding, error }
}
